import './polyfills';
import React, { type RefObject } from 'react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import './adobeFonts.css';
import './index.css';
import { StylesProvider } from '@material-ui/styles';
import createGenerateClassName from '@material-ui/styles/createGenerateClassName';
import axios from 'axios';
import ReactDOM from 'react-dom';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { pdfjs } from 'react-pdf';
import { Provider } from 'react-redux';
import { API_URL, Events } from './common/constants';
import type { IEventEmitter } from './common/types';
import { DigiSignAdsProvider } from './components/DigiSignAdsProvider';
// eslint-disable-next-line import/no-named-as-default -- connect & withStyles
import Signer from './containers/Signer';
import EventEmitter from './lib/EventEmitter';
import { decodeToken } from './lib/token';
import { initializeLD } from './resources/launchDarkly';
import configureStore from './store/index';
import theme from './theme';
import './lib/faro';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const generateClassName = createGenerateClassName({
  productionPrefix: 'ds',
  seed: 'digisign3',
});

interface ILibraryOptions {
  envelopeId: string;
  signerId: string;
  apiKey: string;
  accessToken: string;
  elementId?: string;
  element?: Element;
  elementRef?: RefObject<HTMLElement>;
  showSuccessScreen?: boolean;
}

interface IDocumentOptions {
  apiKey: string;
  accessToken: string;
  envelopeId: string;
  documentId: string;
}

interface ICheckEnvelopeOptions {
  apiKey: string;
  accessToken: string;
  envelopeId: string;
  signerId: string;
}

export let renderTarget: Element;
async function init(options: ILibraryOptions) {
  const { envelopeId, signerId, showSuccessScreen = true } = options;
  const store = configureStore();

  if (options.element) {
    renderTarget = options.element;
  } else if (options.elementId) {
    renderTarget = document.getElementById(options.elementId)!;
  } else if (options.elementRef) {
    renderTarget = options.elementRef.current!;
  } else {
    throw 'DigiSign: Missing one of: `elementId`, `element`, or `elementRef`';
  }

  const props = {
    signerId,
    envelopeId,
    showSuccessScreen,
  };

  await initializeLD();

  ReactDOM.render(
    <Provider store={store}>
      <StylesProvider generateClassName={generateClassName}>
        <MuiThemeProvider theme={theme}>
          <DigiSignAdsProvider>
            <Signer {...props} />
          </DigiSignAdsProvider>
        </MuiThemeProvider>
      </StylesProvider>
    </Provider>,
    renderTarget
  );
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const validateMountOptions = (options: ILibraryOptions): boolean => {
  const { envelopeId, signerId, apiKey, accessToken } = options;
  if (
    typeof envelopeId === 'string' &&
    typeof signerId === 'string' &&
    typeof apiKey === 'string' &&
    typeof accessToken === 'string'
  ) {
    return true;
  }
  return false;
};

let eventEmitter: IEventEmitter = new EventEmitter();
let apiKey: string;
let accessToken: string;

const lib = {
  mount: (options: ILibraryOptions) => {
    try {
      const { EnvelopeId: envelopeId = options.envelopeId, SignerId: signerId = options.signerId } = JSON.parse(
        atob(options.accessToken.split('.')[1])
      );
      options.envelopeId = envelopeId;
      options.signerId = signerId;

      if (!validateMountOptions(options)) {
        throw new Error('Oho! The options you provided are not valid!');
      }

      apiKey = options.apiKey;
      accessToken = options.accessToken;
      decodeToken(accessToken);
      init(options);

      return eventEmitter;
    } catch {
      throw new Error('The accessToken provided is invalid!');
    }
  },
  unmount: (element: HTMLElement) => {
    try {
      ReactDOM.unmountComponentAtNode(element);
    } catch {
      eventEmitter.emit(Events.ERROR, 'Please provide a valid HTMLElement to unmount');
    }
  },
  getPresignedDocumentUrl: async (options: IDocumentOptions) => {
    const { apiKey, accessToken, envelopeId, documentId } = options;
    try {
      const response = await axios.get(`${API_URL}/envelopes/${envelopeId}/documents/${documentId}/downloadURL`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-api-key': apiKey,
        },
      });
      return response.data.result.preSignedDownloadUrl;
    } catch (error: any) {
      throw error.response.data;
    }
  },
  isEnvelopeReadyForSigning: async (options: ICheckEnvelopeOptions) => {
    const { apiKey, accessToken, envelopeId, signerId } = options;
    try {
      const response = await axios.get(`${API_URL}/envelopes/${envelopeId}/signer/${signerId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-api-key': apiKey,
        },
      });
      return response.data;
    } catch (error: any) {
      return error.response.data;
    }
  },
};

export { lib, eventEmitter, apiKey, accessToken };

if (process.env.NODE_ENV === 'development') {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  window.DigiSign = lib;
}
